import { useState, useMemo, useEffect, useRef } from 'react';
import { parse } from '@atlassian/cs-ari';
import type ApolloClient from 'apollo-client';
import FeatureGates from '@atlaskit/feature-gate-js-client';
import { type CoreData, type ExtensionData } from '@atlassian/forge-ui-types';
import type { DocNode } from '@atlaskit/adf-schema';

import { useInvokeExtension } from '@atlassian/forge-ui';

export const ADF_EXPORT_CONTEXT_TOKEN_EXPERIMENT = 'forge_adf_export_context_token';

export interface useInvokeAdfExportExtensionProps {
	accountId: string;
	apolloClient: ApolloClient<object>;
	contextIds: string[];
	coreData: CoreData;
	extensionData: ExtensionData;
	extensionId: string;
	extensionPayload?: Record<string, any>;
	/**
	 * Optional function to retrieve Forge Context Token (e.g. from useGetContextToken).
	 * When the experiment is enabled, the token is fetched and included in the invoke payload.
	 */
	getContextToken?: () => Promise<string>;
}

export interface useInvokeAdfExportExtensionResponse {
	adfDoc: DocNode | undefined;
	error: string | undefined;
	isLoading: boolean;
}

function parseExtensionId(extensionId: string) {
	const ari = parse(extensionId);
	const [appId, environmentId, groupId, moduleKey] = ari.resourceId!.split('/');
	return { appId, environmentId, groupId, moduleKey };
}

export const useInvokeAdfExportExtension = ({
	accountId,
	apolloClient,
	contextIds,
	extensionId,
	coreData,
	extensionData,
	extensionPayload,
	getContextToken,
}: useInvokeAdfExportExtensionProps): useInvokeAdfExportExtensionResponse => {
	const [adfDoc, setAdfDoc] = useState<DocNode | undefined>(undefined);
	const [isLoading, setIsLoading] = useState<boolean>(false);
	const [error, setError] = useState<string | undefined>(undefined);

	const memoedContextIds = useMemo(() => contextIds, [contextIds]);

	const { moduleKey } = parseExtensionId(extensionId);
	const siteUrl = window.location.origin;
	const memoedCoreData = useMemo(
		() => ({
			accountId,
			...coreData,
			...extensionData,
			moduleKey,
			siteUrl,
		}),
		[coreData, extensionData, moduleKey, siteUrl, accountId],
	);

	const invoker = useInvokeExtension(memoedContextIds, extensionId, {
		client: apolloClient,
	});

	// Keep getContextToken in a ref so the effect doesn't re-run when the parent passes a new reference.
	const getContextTokenRef = useRef(getContextToken);

	useEffect(() => {
		const context = {
			...memoedCoreData,
		};
		const fetchAdfExport = async () => {
			setIsLoading(true);

			try {
				const shouldReportResult = FeatureGates.getExperimentValue(
					ADF_EXPORT_CONTEXT_TOKEN_EXPERIMENT,
					'isEnabled',
					false,
				);

				let contextToken: string | undefined;
				if (shouldReportResult) {
					const getToken = getContextTokenRef.current;
					contextToken = getToken ? await getToken() : undefined;
				}

				const mutationResult = await invoker(
					{
						context,
						extensionPayload,
						exportType: 'pdf',
						...(contextToken ? { contextToken } : {}),
					},
					'adfExport',
				);

				const response = mutationResult.data.invokeExtension.response || {};

				if (response?.body) {
					setAdfDoc(response.body);
				}
				setError(response?.error);
			} catch (error) {
				setError(error && (error as any).message);
			}
			setIsLoading(false);
		};
		fetchAdfExport();
	}, [apolloClient, invoker, memoedCoreData, extensionPayload]);
	return { adfDoc, isLoading, error };
};
